import React from "react";
import { Icon, Popup } from "semantic-ui-react";
import { EllipsisPopup, type EllipsisPopupOption } from "../../components/buttons/ellipsisPopup/EllipsisPopup";
import { type ColumnOption } from "../../interfaces/columnOptions";
import { type Domain, DomainStatus, DomainTypes } from "../Library/SimulatedPhishing/Domains/types/state";
import { TextTruncate } from "../../components/textTruncators/TextTruncators";

export enum Columns {
  Domain = "Domain",
  DateAdded = "Date added",
  Status = "Status",
  DomainType = "Domain type",
  UsedBy = "Used by",
}

export const columnToParamMap: {
  [P in Lowercase<Columns>]?: string;
} = {
  domain: "domain",
  "date added": "dateAdded",
  status: "status",
  "domain type": "domainType",
};

const domainTypeLabels: Record<DomainTypes, string> = {
  [DomainTypes.Shared]: "Shared",
  [DomainTypes.Dedicated]: "Dedicated",
};

const domainStatusLabels: Record<DomainStatus, string> = {
  [DomainStatus.Available]: "Available",
  [DomainStatus.Used]: "Used",
};

interface GetColumnOptionsProps {
  handleEdit: (id: number) => void;
  handleDelete: (id: number) => void;
}

export const getColumnOptions = (props: GetColumnOptionsProps): ColumnOption<Domain>[] => {
  const { handleEdit, handleDelete } = props;

  const getEllipsisOptions = (domainId: number): EllipsisPopupOption[] => [
    {
      label: "Edit",
      iconName: "fal fa-edit",
      onClick: () => handleEdit(domainId),
    },
    {
      label: "Delete",
      iconName: "fal fa-trash-alt",
      onClick: () => handleDelete(domainId),
    },
  ];

  return [
    {
      name: Columns.Domain,
      width: 3,
      isSortable: true,
      className: "domain-name-cell",
      render: (domain) => (
        <Popup
          content={domain.name}
          trigger={<div className="domain-name-text">{domain.name}</div>}
          position="top left"
          inverted
          disabled={domain.name.length <= 50}
        />
      ),
    },
    {
      name: Columns.DateAdded,
      width: 3,
      isSortable: true,
      render: (domain) => domain.dateAdded,
    },
    {
      name: Columns.Status,
      width: 3,
      isSortable: true,
      render: (domain) => (
        <div className="status-cell">
          <Icon
            name="circle"
            className={`status-icon ${domain.status === DomainStatus.Available ? "available" : "used"}`}
          />
          <span className="status-label">{domainStatusLabels[domain.status]}</span>
        </div>
      ),
    },
    {
      name: Columns.DomainType,
      width: 3,
      isSortable: true,
      render: (domain) => domainTypeLabels[domain.type],
    },
    {
      name: Columns.UsedBy,
      width: 2,
      isSortable: false,
      className: "used-by-cell",
      render: (domain) => (
        <>
          {domain.usedBy && domain.usedBy.length > 0 && (
            <Popup
              content={domain.usedBy.map((u) => u.name).join(", ")}
              trigger={
                <TextTruncate lines={2} className="used-by-text">
                  {domain.usedBy.map((u) => u.name).join(", ")}
                </TextTruncate>
              }
              position="top left"
              inverted
            />
          )}
        </>
      ),
    },
    {
      name: "",
      width: 1,
      isSortable: false,
      className: "actions-cell",
      render: (domain) => (
        <div onClick={(e) => e.preventDefault()}>
          <EllipsisPopup
            options={getEllipsisOptions(domain.id)}
            disabled={false}
            testId={`domain-actions-${domain.id}`}
          />
        </div>
      ),
    },
  ];
};
